// import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import { useDispatch, useSelector } from 'react-redux';
// import { useNavigate, useLocation } from 'react-router-dom';
// import { setLanguage } from '../../../store/LanguageSlice';
// import LanguageSwitcherSkeleton from './LanguageSwitcherSkeleton';
// import { useCachedFetch } from './../../../hooks/useCachedFetch';
// import './LanguageSwitcher.scss';

// const LanguageSwitcher = ({ hideLanguageSwitcher = false }) => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const location = useLocation();
//   const currentLang = useSelector(state => state.language.current);
//   const supportedLanguages = useSelector(state => state.language.supported);

//   const { data: jsonData, loading: dataLoading, error: dataError } = useCachedFetch('/data/locales/languageSwitcher.json');
  
//   const [isLoading, setIsLoading] = useState(true);
//   const [hasData, setHasData] = useState(false);
//   const [languageData, setLanguageData] = useState([]);
//   const [isOpen, setIsOpen] = useState(false);

//   useEffect(() => {
//     if (dataLoading) return;

//     if (dataError) {
//       console.error('Error loading language switcher data:', dataError);
//       setHasData(false);
//       setIsLoading(false);
//       return;
//     }

//     try {
//       const loadedLanguageData = jsonData?.LanguageSwitcherData;

//       if (loadedLanguageData && 
//           Array.isArray(loadedLanguageData) && 
//           loadedLanguageData.length > 0 &&
//           currentLang &&
//           supportedLanguages &&
//           Array.isArray(supportedLanguages) &&
//           supportedLanguages.length > 0) {
//         setLanguageData(loadedLanguageData);
//         setHasData(true);
//       } else {
//         setHasData(false);
//         console.warn('Invalid language switcher data structure');
//       }
//     } catch (error) {
//       console.error('Error processing language switcher data:', error);
//       setHasData(false);
//     } finally {
//       setIsLoading(false);
//     }
//   }, [jsonData, dataLoading, dataError, currentLang, supportedLanguages]);

//   const handleCurrentClick = useCallback(() => {
//     setIsOpen(prev => !prev);
//   }, []);

//   const handleSelect = useCallback((code) => {
//     if (code === currentLang) {
//       setIsOpen(false);
//       return;
//     }

//     const pathParts = location.pathname.split('/').filter(Boolean);
//     let newPath;
    
//     if (pathParts.length > 0 && supportedLanguages.includes(pathParts[0])) {
//       pathParts[0] = code;
//       newPath = '/' + pathParts.join('/');
//     } else {
//       newPath = `/${code}${location.pathname}`;
//     }
    
//     navigate(newPath, { replace: true });
//     dispatch(setLanguage(code));
//     setIsOpen(false);
//   }, [currentLang, location.pathname, supportedLanguages, navigate, dispatch]);

//   const currentLanguage = useMemo(() => {
//     return languageData.find(l => l.code === currentLang);
//   }, [languageData, currentLang]);

//   const availableLanguages = useMemo(() => {
//     return languageData.filter(l => l.code !== currentLang);
//   }, [languageData, currentLang]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (isOpen && !event.target.closest('.language-switcher')) {
//         setIsOpen(false);
//       }
//     };

//     document.addEventListener('click', handleClickOutside);
//     return () => document.removeEventListener('click', handleClickOutside);
//   }, [isOpen]);

//   useEffect(() => {
//     setIsOpen(false);
//   }, [location.pathname]);

//   if (hideLanguageSwitcher) {
//     return null;
//   }

//   if (isLoading) {
//     return <LanguageSwitcherSkeleton />;
//   }

//   if (!hasData || !languageData.length) {
//     return null;
//   }

//   return (
//     <div
//       className={`language-switcher ${isOpen ? 'open' : ''}`}
//       onMouseLeave={() => setIsOpen(false)}
//     >
//       <button 
//         className="current-lang" 
//         onClick={handleCurrentClick}
//         aria-label={`Current language: ${currentLanguage?.label}. Click to change language`}
//         aria-expanded={isOpen}
//         aria-haspopup="true"
//       >
//         {currentLanguage?.label}
//       </button>

//       {isOpen && (
//         <div 
//           className="language-list"
//           role="menu"
//           aria-label="Available languages"
//         >
//           {availableLanguages.map(lang => (
//             <button
//               key={lang.code}
//               className="language-item"
//               onClick={() => handleSelect(lang.code)}
//               role="menuitem"
//               aria-label={`Switch to ${lang.label}`}
//             >
//               {lang.label}
//             </button>
//           ))}
//         </div>
//       )}
//     </div>
//   );
// };

// export default React.memo(LanguageSwitcher);




import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { setLanguage } from '../../../store/LanguageSlice';
import LanguageSwitcherSkeleton from './LanguageSwitcherSkeleton';
import { useCachedFetch } from './../../../hooks/useCachedFetch';
import './LanguageSwitcher.scss';

const LanguageSwitcher = ({ hideLanguageSwitcher = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLang = useSelector(state => state.language.current);
  const supportedLanguages = useSelector(state => state.language.supported);

  const { data: jsonData, loading: dataLoading, error: dataError } = useCachedFetch('/data/locales/languageSwitcher.json');
  
  const [isLoading, setIsLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [languageData, setLanguageData] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  // ⭐ СИНХРОНИЗАЦИЯ REDUX С URL
  useEffect(() => {
    const pathLang = location.pathname.match(/^\/(ua|en|ru|pl|de|es)/)?.[1];
    if (pathLang && pathLang !== currentLang) {
      dispatch(setLanguage(pathLang));
    }
  }, [location.pathname, currentLang, dispatch]);

  useEffect(() => {
    if (dataLoading) return;

    if (dataError) {
      console.error('Error loading language switcher data:', dataError);
      setHasData(false);
      setIsLoading(false);
      return;
    }

    try {
      const loadedLanguageData = jsonData?.LanguageSwitcherData;

      if (loadedLanguageData && 
          Array.isArray(loadedLanguageData) && 
          loadedLanguageData.length > 0 &&
          currentLang &&
          supportedLanguages &&
          Array.isArray(supportedLanguages) &&
          supportedLanguages.length > 0) {
        setLanguageData(loadedLanguageData);
        setHasData(true);
      } else {
        setHasData(false);
        console.warn('Invalid language switcher data structure');
      }
    } catch (error) {
      console.error('Error processing language switcher data:', error);
      setHasData(false);
    } finally {
      setIsLoading(false);
    }
  }, [jsonData, dataLoading, dataError, currentLang, supportedLanguages]);

  const handleCurrentClick = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleSelect = useCallback((code) => {
    if (code === currentLang) {
      setIsOpen(false);
      return;
    }

    const pathParts = location.pathname.split('/').filter(Boolean);
    let newPath;
    
    if (pathParts.length > 0 && supportedLanguages.includes(pathParts[0])) {
      pathParts[0] = code;
      newPath = '/' + pathParts.join('/');
    } else {
      newPath = `/${code}${location.pathname}`;
    }
    
    navigate(newPath, { replace: true });
    dispatch(setLanguage(code));
    setIsOpen(false);
  }, [currentLang, location.pathname, supportedLanguages, navigate, dispatch]);

  const currentLanguage = useMemo(() => {
    return languageData.find(l => l.code === currentLang);
  }, [languageData, currentLang]);

  const availableLanguages = useMemo(() => {
    return languageData.filter(l => l.code !== currentLang);
  }, [languageData, currentLang]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.language-switcher')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  if (hideLanguageSwitcher) {
    return null;
  }

  if (isLoading) {
    return <LanguageSwitcherSkeleton />;
  }

  if (!hasData || !languageData.length) {
    return null;
  }

  return (
    <div
      className={`language-switcher ${isOpen ? 'open' : ''}`}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button 
        className="current-lang" 
        onClick={handleCurrentClick}
        aria-label={`Current language: ${currentLanguage?.label}. Click to change language`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {currentLanguage?.label}
      </button>

      {isOpen && (
        <div 
          className="language-list"
          role="menu"
          aria-label="Available languages"
        >
          {availableLanguages.map(lang => (
            <button
              key={lang.code}
              className="language-item"
              onClick={() => handleSelect(lang.code)}
              role="menuitem"
              aria-label={`Switch to ${lang.label}`}
            >
              {lang.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(LanguageSwitcher);